import { useState, useEffect } from "react";
import { AlertTriangle, Calendar, Globe, X } from "lucide-react";
import { supabase } from "./supabase";
import { dispType, getHolidays } from "./helpers";

export function CalendarView({t, lang, isAdmin, empName, refreshKey}){
  const [cur,setCur]=useState(new Date());
  const [leaves,setLeaves]=useState([]);
  const [employees,setEmployees]=useState([]);
  const [calError,setCalError]=useState(null);
  const [mobilePopup,setMobilePopup]=useState(null); // {d, ds, leaves, holiday}
  const isMobile=typeof window!=="undefined"&&window.innerWidth<=768;
  const year=cur.getFullYear(), month=cur.getMonth();
  const holidays=getHolidays(year);

  useEffect(()=>{
    supabase.from("leaves").select("*").is("timesheet",null).then(({data,error})=>{ if(error){setCalError(error.message);return;} if(data) setLeaves(data.filter(l=>(l.type==="연차"||l.type==="Annual Leave")?(l.status==="approved"):true)); });
    supabase.from("employees").select("name").then(({data})=>{ if(data) setEmployees(data); });
  },[refreshKey]);

  const first=new Date(year,month,1).getDay();
  const days=new Date(year,month+1,0).getDate();
  const ES=lang==="es";
  const monthNames=ES?["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"]:["January","February","March","April","May","June","July","August","September","October","November","December"];
  const dayNames=ES?["Dom","Lun","Mar","Mié","Jue","Vie","Sáb"]:["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const holidayEs={"New Year's Day":"Año Nuevo","Martin Luther King Jr. Day":"Día de Martin Luther King Jr.","Presidents' Day":"Día de los Presidentes","Memorial Day":"Día de los Caídos","Juneteenth":"Juneteenth","Independence Day":"Día de la Independencia","Labor Day":"Día del Trabajo","Columbus Day":"Día de la Raza (Colón)","Veterans Day":"Día de los Veteranos","Thanksgiving Day":"Día de Acción de Gracias","Christmas Day":"Navidad"};
  const holName=(n)=>{ if(!ES||!n) return n; const obs=n.endsWith(" (observed)"); const b=obs?n.slice(0,-11):n; return (holidayEs[b]||b)+(obs?" (observado)":""); };
  const typeColors={"연차":"#3b82f6","병가":"#ef4444","특이사항":"#6b7280","Annual Leave":"#3b82f6","Sick Leave":"#ef4444","Special Note":"#6b7280","Note":"#6b7280"};
  const KO2=lang==="kr"; const legendItems=KO2?[["연차","#3b82f6"],["병가","#ef4444"],["특이사항","#6b7280"]]:ES?[["Vacaciones","#3b82f6"],["Baja por enfermedad","#ef4444"],["Nota","#6b7280"]]:[["Annual Leave","#3b82f6"],["Sick Leave","#ef4444"],["Note","#6b7280"]];

  const cells=[];
  for(let i=0;i<first;i++) cells.push(null);
  for(let d=1;d<=days;d++) cells.push(d);
  while(cells.length%7!==0) cells.push(null);

  function getDayLeaves(d){
    if(!d) return [];
    const ds=`${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
    const dow=new Date(ds+"T00:00:00").getDay();
    if(dow===0||dow===6||holidays[ds]) return [];
    return (leaves||[]).filter(l=>l&&l.date<=ds&&(l.end_date||l.date)>=ds);
  }

  function isHoliday(d){
    if(!d) return null;
    const ds=`${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
    const name=holidays[ds];
    return name?{date:ds,name}:null;
  }

  const prev=()=>setCur(new Date(year,month-1,1));
  const next=()=>setCur(new Date(year,month+1,1));
  const today=new Date();
  if(calError) return <div className="card"><div className="empty" style={{color:"var(--re)"}}><AlertTriangle size={14} style={{marginRight:4}}/>Calendar error: {calError}</div></div>;

  return(
    <>
      <div className="ph"><div className="pt"><Calendar size={20}/> {t.nav.cal}</div></div>
      <div className="card" style={{maxWidth:"100%"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <button className="btn bsec" onClick={prev}>‹</button>
          <div style={{fontWeight:800,fontSize:18}}>{monthNames[month]} {year}</div>
          <button className="btn bsec" onClick={next}>›</button>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:4}}>
          {dayNames.map(d=><div key={d} style={{textAlign:"center",fontSize:11,fontWeight:700,color:"var(--tx3)",padding:"4px 0"}}>{d}</div>)}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2}}>
          {cells.map((d,i)=>{
            const dayLeaves=getDayLeaves(d);
            const holiday=isHoliday(d);
            const isToday=d&&year===today.getFullYear()&&month===today.getMonth()&&d===today.getDate();
            const isSun=i%7===0, isSat=i%7===6;
            const ds2=d?`${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`:"";
            return(
              <div key={i} onClick={()=>{if(d&&isMobile) setMobilePopup({d,ds:ds2,dayLeaves,holiday});}}
                style={{height:80,padding:"4px 6px",background:isToday?"rgba(59,130,246,.1)":"var(--sf2)",borderRadius:6,border:isToday?"1.5px solid var(--ac)":"1px solid var(--bd)",opacity:d?1:0,overflow:"hidden",cursor:d&&isMobile?"pointer":"default"}}>
                {d&&<div style={{fontSize:12,fontWeight:isToday?800:500,color:holiday||isSun?"var(--re)":isSat?"#3b82f6":"var(--tx)",marginBottom:2}}>{d}</div>}
                {holiday&&<div style={{fontSize:9,color:"var(--re)",fontWeight:600,lineHeight:1.3,marginBottom:2,wordBreak:"keep-all",whiteSpace:"normal"}}>{holName(holiday.name)}</div>}
                {dayLeaves.map((l,li)=>(
                  <div key={li} style={{fontSize:9,padding:"1px 4px",borderRadius:3,marginBottom:1,background:typeColors[l.type]||"#6b7280",color:"#fff",overflow:"hidden",whiteSpace:"nowrap",textOverflow:"ellipsis"}}>
                    {l.emp_name||l.name||""} - {dispType(l.type,lang)}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
        <div style={{marginTop:12,display:"flex",gap:8,flexWrap:"wrap"}}>
          {legendItems.map(([type,color])=>(
            <span key={type} style={{fontSize:11,display:"flex",alignItems:"center",gap:4}}>
              <span style={{width:10,height:10,borderRadius:2,background:color,display:"inline-block"}}></span>{type}
            </span>
          ))}
        </div>
      </div>
      {mobilePopup&&(
        <div onClick={()=>setMobilePopup(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.45)",zIndex:900,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"var(--sf)",borderRadius:"16px 16px 0 0",padding:"20px 20px 32px",width:"100%",maxHeight:"60vh",overflowY:"auto"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div style={{fontWeight:800,fontSize:17}}>{mobilePopup.ds}</div>
              <button onClick={()=>setMobilePopup(null)} style={{background:"none",border:"none",fontSize:22,cursor:"pointer",color:"var(--tx3)"}}><X size={20}/></button>
            </div>
            {mobilePopup.holiday&&(
              <div style={{marginBottom:10,padding:"8px 12px",background:"rgba(239,68,68,.08)",borderRadius:8,border:"1px solid rgba(239,68,68,.2)"}}>
                <span style={{fontSize:13,color:"var(--re)",fontWeight:700}}><Globe size={13}/> {holName(mobilePopup.holiday.name)}</span>
              </div>
            )}
            {mobilePopup.dayLeaves.length===0&&!mobilePopup.holiday&&(
              <div style={{fontSize:14,color:"var(--tx3)",textAlign:"center",padding:"20px 0"}}>{lang==="kr"?"이 날 휴가/특이사항 없음":ES?"Sin ausencias ni notas este día":"No leave records for this day"}</div>
            )}
            {mobilePopup.dayLeaves.map((l,i)=>(
              <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:"1px solid var(--bd)"}}>
                <div>
                  <div style={{fontWeight:700,fontSize:14}}>{l.emp_name||l.name}</div>
                  {l.reason&&(isAdmin||(l.emp_name===empName))&&<div style={{fontSize:12,color:"var(--tx3)",marginTop:2}}>{l.reason}</div>}
                </div>
                <span style={{padding:"3px 10px",borderRadius:20,fontSize:12,fontWeight:700,background:typeColors[l.type]||"#6b7280",color:"#fff"}}>{dispType(l.type,lang)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}


